import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useStorageQuota } from '../../hooks/useStorageQuota';
import { 
  HardDrive, 
  Package, 
  Plus,
  Minus,
  X,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { getPlanByTier } from '../../config/subscriptionPlans';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

export default function StorageAddon() {
  const { user } = useAuthContext();
  const { plan, tier } = useSubscription();
  const { usedGB, limitGB } = useStorageQuota();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [addonStorageGB, setAddonStorageGB] = useState<number>(0);
  const [currentAddons, setCurrentAddons] = useState<any[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        setCurrentAddons(userData?.addons || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const calculateAddonPrice = (gb: number) => {
    if (gb <= 0) return 0;
    const units = gb / 5;
    const rawPrice = units * 99;
    if (gb === 5) return 99;
    return Math.floor(rawPrice / 10) * 10;
  };

  const calculateTotalStorage = () => {
    const planStorage = (getPlanByTier(tier)?.features.storageGB.videoStorage || 0) / 1024;
    const currentAddonsStorage = Array.isArray(currentAddons) 
      ? currentAddons.reduce((sum, addon) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
      : 0;
    return planStorage + currentAddonsStorage + addonStorageGB;
  };

  const handleProceedToPayment = () => {
    if (addonStorageGB <= 0) return;

    const checkoutData = {
      plan: tier || 'free',
      planName: plan?.name || 'Free Trial (14 วัน)',
      planPrice: 0,
      storageAddon: addonStorageGB,
      total: calculateAddonPrice(addonStorageGB),
      isRenewal: false,
      isAddon: true,
      isStorageOnly: true,
      returnTo: '/subscription/storage-addon'
    };
    
    console.log('💾 [StorageAddon] Saving to localStorage:', checkoutData);
    localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutData));
    
    navigate(`/subscription/checkout-new?tier=${tier || 'free'}&storage=true`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-cyan-600 mx-auto" />
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  const currentAddonsStorage = Array.isArray(currentAddons) 
    ? currentAddons.reduce((sum, addon) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <HardDrive className="w-8 h-8 text-orange-600" />
            Add-on Storage
          </h1>
          <p className="text-gray-600 mt-2">เพิ่มพื้นที่จัดเก็บข้อมูลสำหรับคอร์สของคุณ</p>
        </div>

        {/* Main Grid Layout - 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Current Package Info - No Price */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-cyan-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-cyan-600" />
                รายละเอียดพื้นที่จัดเก็บ
              </h2>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">แพ็คเกจปัจจุบัน</p>
                    <p className="text-xl font-bold text-gray-900">{plan?.name || 'FREE'}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      พื้นที่จากแพ็คเกจ: {((getPlanByTier(tier)?.features.storageGB.videoStorage || 0) / 1024).toFixed(0)} GB
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">พื้นที่ใช้งาน</p>
                    <p className="text-2xl font-bold text-cyan-600">
                      {usedGB.toFixed(2)} / {limitGB.toFixed(0)} GB
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div 
                        className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (usedGB / limitGB) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Current Add-ons */}
                {currentAddonsStorage > 0 && (
                  <div className="bg-orange-50 rounded-lg p-3 mt-4">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Add-on Storage ปัจจุบัน</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">พื้นที่เพิ่มเติมที่ซื้อแล้ว</span>
                      <span className="text-lg font-bold text-orange-600">+{currentAddonsStorage} GB</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Add Storage Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-orange-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-orange-600" />
                เพิ่มพื้นที่จัดเก็บ
              </h2>
              
              <p className="text-sm text-gray-600 mb-4">
                เลือกพื้นที่เพิ่มเติมที่ต้องการซื้อ (ซื้อครั้งเดียว ไม่ต้องต่ออายุ)
              </p>
              
              {/* Storage Input with +/- buttons */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-700">จำนวนพื้นที่ที่ต้องการเพิ่ม</label>
                  {addonStorageGB > 0 && (
                    <button
                      onClick={() => setAddonStorageGB(0)}
                      className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 font-medium"
                    >
                      <X className="w-3 h-3" />
                      ยกเลิก
                    </button>
                  )}
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <button
                    onClick={() => {
                      const newValue = Math.max(0, addonStorageGB - 5);
                      setAddonStorageGB(newValue);
                    }}
                    disabled={addonStorageGB <= 0}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Minus className="w-5 h-5 text-orange-600" />
                  </button>
                  
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={addonStorageGB}
                      onChange={(e) => {
                        const input = e.target.value.replace(/[^0-9]/g, '');
                        const value = input === '' ? 0 : parseInt(input);
                        if (value >= 0 && value <= 500) {
                          setAddonStorageGB(value);
                        }
                      }}
                      onBlur={(e) => {
                        const input = e.target.value.replace(/[^0-9]/g, '');
                        const value = input === '' ? 0 : parseInt(input);
                        const rounded = Math.round(value / 5) * 5;
                        setAddonStorageGB(Math.max(0, Math.min(500, rounded)));
                      }}
                      className="w-full px-4 py-3 text-center text-2xl font-bold border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:outline-none"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-600">GB</span>
                  </div>
                  
                  <button
                    onClick={() => {
                      const newValue = Math.min(500, addonStorageGB + 5);
                      setAddonStorageGB(newValue);
                    }}
                    disabled={addonStorageGB >= 500}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Plus className="w-5 h-5 text-orange-600" />
                  </button>
                </div>

                {/* Price Calculation */}
                {addonStorageGB > 0 ? (
                  <div className="flex items-center justify-between pt-3 border-t-2 border-orange-200">
                    <span className="text-lg font-semibold text-gray-900">ราคารวม:</span>
                    <span className="text-3xl font-bold text-orange-600">
                      ฿{calculateAddonPrice(addonStorageGB).toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    กรุณาเลือกจำนวนพื้นที่ที่ต้องการเพิ่ม (ทีละ 5GB)
                  </p>
                )}
              </div>
            </div>

          </div>
          {/* End Left Column */}

          {/* Right Column - Sticky Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {addonStorageGB > 0 ? (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-lg p-6 border-2 border-cyan-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">สรุปรายการ</h3>
                  
                  {/* Total Price */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">ยอดรวมทั้งหมด</p>
                    <p className="text-4xl font-bold text-orange-600">
                      ฿{calculateAddonPrice(addonStorageGB).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">ซื้อครั้งเดียว</p>
                  </div>

                  {/* Storage Breakdown */}
                  <div className="mb-6 pb-6 border-b border-cyan-200">
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">จากแพ็คเก็จ:</span>
                        <span className="font-semibold text-gray-700">
                          {((getPlanByTier(tier)?.features.storageGB.videoStorage || 0) / 1024).toFixed(0)} GB
                        </span>
                      </div>
                      {currentAddonsStorage > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Add-on เดิม:</span>
                          <span className="font-semibold text-gray-700">
                            +{currentAddonsStorage} GB
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">เพิ่มใหม่:</span>
                        <span className="font-semibold text-orange-600">
                          +{addonStorageGB} GB
                        </span>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-cyan-200">
                      <p className="text-sm text-gray-600 mb-2">พื้นที่รวมทั้งหมด</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {calculateTotalStorage().toFixed(0)} GB
                      </p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Storage +{addonStorageGB}GB</span>
                      <span className="font-semibold text-orange-600">
                        ฿{calculateAddonPrice(addonStorageGB).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Proceed Button */}
                  <button
                    onClick={handleProceedToPayment}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    ดำเนินการชำระเงิน
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200">
                  <p className="text-center text-gray-500 text-sm">
                    เลือกจำนวนพื้นที่ที่ต้องการเพิ่มเพื่อดูสรุปราคา
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* End Right Column */}

        </div>
        {/* End Grid Layout */}
      </div>
    </div>
  );
}
